import { InteractShareIcon, InteractWatchIcon } from '@/client/assets';
import { LocalizedText } from '@/client/components/localized-text';
import { CurrencyView } from '@/client/components/currency-view';
import { FollowMarket } from '@/client/components/follow-market';
import { MarketDeadline } from '@/client/components/market-deadline';
import { OptionButton } from '@/client/components/option-button';
import { ShareMarket } from '@/client/components/share-market';
import { TextTooltip } from '@/client/components/text-tooltip';
import { Skeleton } from '@/client/components/ui/skeleton';
import { cn } from '@/client/utils';
import { categoryLabel } from '@/client/utils/category-i18n';
import { FollowStatus, Market, MarketStatus } from '@/libs/sdk/api';
import { isClobMarketAddress, extractConditionId } from '@/client/clob/adapter';
import { memo, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import useInViewport from 'ahooks/lib/useInViewport';
import { getBinaryMarketOptions, getBinaryProbabilityPercentage } from './binary-options';

export const MarketCardSkeleton = memo(() => (
  <div className="space-y-3 p-5 w-full mcard-surface rounded-2xl">
    <div className="flex items-start gap-3">
      <Skeleton className="size-9 rounded-md flex-shrink-0" />
      <Skeleton className="flex-1 h-9" />
    </div>
    <Skeleton className="w-20 h-4" />
    <div className="flex gap-2">
      <Skeleton className="flex-1 h-8 rounded-md" />
      <Skeleton className="flex-1 h-8 rounded-md" />
    </div>
    <div className="flex items-center justify-between">
      <Skeleton className="w-16 h-3" />
      <Skeleton className="w-24 h-3" />
    </div>
  </div>
));
MarketCardSkeleton.displayName = 'MarketGridCardSkeleton';

type MarketCardProps = {
  className?: string;
  market: Market;
  index?: number;
  // presentational, derived upstream from visible-list volume (see hot.ts)
  hot?: boolean;
};

export const MarketCard = ({ className, market, index = 0, hot = false }: MarketCardProps) => {
  const { t } = useTranslation(['translation', 'home']);
  const [, navigate] = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [imgOk, setImgOk] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);
  
  // CLOB markets use /clob/:conditionId; APMM uses /market/:address
  const isClob = isClobMarketAddress(market.marketAddress);
  const marketDetailPath = isClob
    ? `/clob/${extractConditionId(market.marketAddress)}`
    : `/market/${market.marketAddress}`;
  
  // Intersection observer for fade-in animation
  const [inViewport] = useInViewport(cardRef, {
    threshold: 0.1,
  });
  
  useEffect(() => {
    if (inViewport && !isVisible) {
      // Stagger delay based on index (max 8 items per row * 50ms)
      const delay = Math.min(index % 8, 7) * 50;
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    }
  }, [inViewport, isVisible, index]);
  
  const { yes, no } = useMemo(() => getBinaryMarketOptions(market.options), [market.options]);
  const resultOption = useMemo(
    () => market?.options.find(item => item.tokenAddress === market?.result),
    [market?.options, market?.result]
  );
  
  // Calculate probability percentage for the progress bar
  const probability = useMemo(() => {
    return getBinaryProbabilityPercentage(market.options) ?? 50;
  }, [market.options]);

  if (!yes || !no) return null;
  
  return (
    <div
      ref={cardRef}
      className={cn(
        // mcard-neon: animated diagonal streaks + sheen sweep + neon hover (design handoff)
        // mcard-surface: translucent navy gradient "inside colour" (client feedback)
        'mcard-neon mcard-surface group p-5 w-full flex flex-col gap-3.5',
        'rounded-2xl',
        'transition-all duration-200 ease-out',
        // Always painted; neon glow pulse is purely additive on scroll-into-view
        'opacity-100 translate-y-0',
        isVisible && 'mcard-reveal-glow in',
        className
      )}
    >
      {/* Top row: thumbnail + (category chip + question) */}
      <div className="flex items-start gap-3">
        {market.marketPicUrl && imgOk && (
          <img
            src={market.marketPicUrl}
            alt=""
            loading="lazy"
            onError={() => setImgOk(false)}
            onClick={() => navigate(marketDetailPath)}
            className="size-10 rounded-lg object-cover shrink-0 cursor-pointer border border-border"
          />
        )}
        <div className="min-w-0 flex-1 space-y-1.5">
          {(hot || market.categoryName) && (
            <span className="inline-flex items-center gap-1.5">
              {hot && <span className="neon-chip hot">● HOT</span>}
              {market.categoryName && <span className="neon-chip cat">{categoryLabel(t, market.categoryName)}</span>}
            </span>
          )}
          <TextTooltip
            tooltip={<LocalizedText i18n={market.nameI18n}>{market.marketName}</LocalizedText>}
            className="neon-q line-clamp-2"
          >
            <Link href={marketDetailPath} className="neon-q-link">
              <LocalizedText i18n={market.nameI18n}>{market.marketName}</LocalizedText>
            </Link>
          </TextTooltip>
        </div>
      </div>

      {/* Trading section for active markets */}
      {market.marketStatus == MarketStatus.progress && (
        <div className="space-y-3">
          {/* Probability % + bar */}
          <div className="neon-prob">
            <div
              className="pct"
              style={{ color: probability >= 50 ? 'var(--neon-yes)' : 'var(--neon-no)' }}
            >
              {probability}%<small>{t('chance')?.toUpperCase()}</small>
            </div>
            <div className="neon-bar">
              <i style={{ width: `${probability}%` }} />
            </div>
          </div>

          {/* Yes/No buttons */}
          <div className="flex gap-2">
            <OptionButton
              marketAddress={market.marketAddress}
              marketDecimal={yes.decimal}
              optionAddress={yes.tokenAddress}
              optionName={yes.tokenName}
              optionSide="yes"
              optionDescription={yes.tokenDescription}
              optionPrice={Number(yes.tokenPrice)}
              optionDecimal={yes.decimal}
              openTrade={!isClob}
              onClick={isClob ? () => navigate(marketDetailPath) : undefined}
              className="flex-1 h-8 rounded-md font-medium text-sm"
            />
            <OptionButton
              marketAddress={market.marketAddress}
              marketDecimal={no.decimal}
              optionAddress={no.tokenAddress}
              optionName={no.tokenName}
              optionSide="no"
              optionDescription={no.tokenDescription}
              optionPrice={Number(no.tokenPrice)}
              optionDecimal={no.decimal}
              openTrade={!isClob}
              onClick={isClob ? () => navigate(marketDetailPath) : undefined}
              className="flex-1 h-8 rounded-md font-medium text-sm"
            />
          </div>
        </div>
      )}
      
      {/* Result section for settled markets */}
      {market.marketStatus >= MarketStatus.settlement && resultOption && (
        <div
          className={cn(
            'mt-3 text-xs font-medium py-1.5 px-2 rounded-md',
            resultOption.tokenAddress === yes.tokenAddress 
              ? 'text-emerald-600 bg-emerald-500/10' 
              : 'text-red-500 bg-red-500/10'
          )}
        >
          {t('results-out-label')} {resultOption.tokenDescription}
        </div>
      )}
      
      {/* Footer with volume, date and actions */}
      <div className="neon-meta mt-auto pt-3">
        <CurrencyView
          className="font-mono"
          value={market.marketVolume}
          decimal={market.marketDecimal}
          baseTokenAddress={market.baseTokenAddress}
        >
          {t('vol')}
        </CurrencyView>
        <div className="flex items-center gap-2">
          <MarketDeadline 
            deadline={Number(market.deadline)} 
            status={market.marketStatus}
            className="text-[11px]"
          />
          <FollowMarket
            variant="ghost"
            iconClassName="p-0"
            className="size-3.5 hover:text-foreground transition-colors"
            watchIcon={<InteractWatchIcon className="size-3.5 fill-current" />}
            isFollowed={market.isFollowed == FollowStatus.yes}
            address={market.marketAddress}
          />
          <ShareMarket
            variant="ghost"
            className="size-3.5 hover:text-foreground transition-colors"
            iconClassName="p-0"
            marketName={market.marketName}
            nameI18n={market.nameI18n}
            marketAddress={market.marketAddress}
          />
        </div>
      </div>
    </div>
  );
};
